import { useEffect, useRef, useState } from 'react';
import { Check } from '@ui/shared/icons';

/**
 * Three-state action-status pill (design/screens/objective-create-desktop.md
 * SS6.2). New component, but reuses `StatusPill`'s exact pill shell
 * (`surface.hover` fill, `type.caption` weight 600, `radius.pill`,
 * `padding: 2px 10px` — DashboardScreen.desktop.tsx's construction) — a
 * direct-manipulation control (opens a 3-option popover), not a read-only
 * label. Never conveys state by color alone — the label text always states
 * the state in words.
 */
export type ActionDisplayStatus = 'to_do' | 'doing' | 'done';

const STATUS_LABEL: Record<ActionDisplayStatus, string> = {
  to_do: 'To do',
  doing: 'Doing',
  done: 'Done',
};

function statusColor(status: ActionDisplayStatus): string {
  switch (status) {
    case 'to_do':
      return 'var(--text-muted)';
    case 'doing':
      return 'var(--hue-secondary)';
    case 'done':
      return 'var(--hue-primary)';
  }
}

export function ActionStatusPill({
  status,
  onChange,
  justCompleted,
}: {
  readonly status: ActionDisplayStatus;
  readonly onChange: (next: ActionDisplayStatus) => void;
  /** Drives the modest inline check-pop on a fresh to_do/doing -> done
   *  transition (SS6.2's "existing completion celebration beat... a modest
   *  inline check-pop, not a full-screen moment"). Respects
   *  prefers-reduced-motion via the global tokens.css override. */
  readonly justCompleted?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: MouseEvent) {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  function handleKeyDown(e: React.KeyboardEvent) {
    const options: ActionDisplayStatus[] = ['to_do', 'doing', 'done'];
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        itemRefs.current[(current + 1 + options.length) % options.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        itemRefs.current[(current - 1 + options.length) % options.length]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Status: ${STATUS_LABEL[status]}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: 'var(--type-caption-size)',
          fontWeight: 600,
          color: statusColor(status),
          background: 'var(--surface-hover)',
          padding: '2px 10px',
          borderRadius: 'var(--radius-pill)',
          whiteSpace: 'nowrap',
          minHeight: 'var(--touch-target-min)',
          transform: justCompleted ? 'scale(1.08)' : 'scale(1)',
          transition: 'transform var(--dur-base) var(--ease-spring)',
        }}
      >
        {status === 'done' && <Check size={12} color="var(--hue-primary)" aria-hidden="true" />}
        {STATUS_LABEL[status]}
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Action status"
          onKeyDown={handleKeyDown}
          style={{
            position: 'absolute',
            zIndex: 41,
            top: 'calc(100% + var(--space-1))',
            right: 0,
            minWidth: '140px',
            background: 'var(--surface-overlay)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-2)',
            border: '1px solid var(--border-subtle)',
            padding: 'var(--space-1)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(['to_do', 'doing', 'done'] as const).map((option, i) => (
            <button
              key={option}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              type="button"
              role="option"
              aria-selected={status === option}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 'var(--touch-target-min)',
                padding: '0 var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                color: statusColor(option),
                fontSize: 'var(--type-caption-size)',
                fontWeight: 600,
                textAlign: 'left',
              }}
            >
              {STATUS_LABEL[option]}
              {status === option && <Check size={14} color={statusColor(option)} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
